import { useState, useEffect } from "react";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent } from "@/components/ui/card";
import { getAnnouncements } from "@/lib/api";
import axios from "axios";
import { AnnouncementBody } from "@/components/shared/AnnouncementBits";
import { motion } from "framer-motion";
import { Bell, AlertTriangle, Info, CheckCircle, Megaphone } from "lucide-react";

const priorityConfig = {
  urgent: { icon: AlertTriangle, color: "bg-red-100 text-red-600 border-red-200", badge: "bg-red-500 text-white" },
  high: { icon: AlertTriangle, color: "bg-orange-100 text-orange-600 border-orange-200", badge: "bg-orange-500 text-white" },
  normal: { icon: Info, color: "bg-blue-100 text-blue-600 border-blue-200", badge: "bg-blue-500 text-white" },
  low: { icon: CheckCircle, color: "bg-green-100 text-green-600 border-green-200", badge: "bg-green-500 text-white" },
};

export default function DealerAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const data = await getAnnouncements(true, "dealers");
        setAnnouncements(data);
        if (data.length) {
          axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/mark-read`, { ids: data.map((a) => a.id) },
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }).catch(() => {});
        }
      } catch (error) {
        console.error("Error fetching announcements:", error);
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const sortedAnnouncements = [...announcements].sort((a, b) => {
    const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  return (
    <DealerLayout title="Announcements" breadcrumb="Announcements">
      <div className="space-y-6" data-testid="dealer-announcements">
        {/* Header */}
        <div className="bg-navy rounded-xl p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
                <Bell className="w-6 h-6" />
                Announcements
              </h2>
              <p className="text-slate-300 font-body">
                Stay up-to-date with the latest news, promotions, and updates from Prime Media Wireless.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
              <Megaphone className="w-5 h-5 text-teal" />
              <span className="font-body text-sm">{announcements.length} Active</span>
            </div>
          </div>
        </div>

        {/* Announcements List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal mx-auto"></div>
            <p className="text-slate-500 mt-4 font-body">Loading announcements...</p>
          </div>
        ) : sortedAnnouncements.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-body">No announcements at this time</p>
            <p className="text-slate-400 text-sm font-body">Check back later for updates</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedAnnouncements.map((announcement, index) => {
              const config = priorityConfig[announcement.priority] || priorityConfig.normal;
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`border-2 ${config.color}`}
                    data-testid={`announcement-${announcement.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-heading font-semibold text-navy">
                              {announcement.title}
                            </h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.badge}`}>
                              {announcement.priority.toUpperCase()}
                            </span>
                          </div>
                          <div className="mb-3"><AnnouncementBody a={announcement} /></div>
                          <p className="text-slate-400 text-sm font-body">
                            Posted {new Date(announcement.created_at).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DealerLayout>
  );
}
